import {
  Box,
  Button,
  ButtonVariants,
  CustomerBalanceTransactionStatusEnum,
  CustomerLedgerUpdateTypeEnum,
  Divider,
  DrawerContent,
  DrawerFooter,
  EMPTY_OBJECT,
  Flex,
  FormControlSizes,
  FormGroup,
  HStack,
  Icon,
  IconButton,
  IconName,
  InlineFormattedNumber,
  Input,
  LoaderTalos,
  NotificationInternalToast,
  NotificationVariants,
  NumberInput,
  SearchSelect,
  Text,
  Toggle,
  ToggleHorizontal,
  TransactionAutoProposalModeEnum,
  VStack,
  useCurrenciesContext,
  useDisclosureWithContext,
} from '@talos/kyoko';

import { useFeatureFlag } from '@src/hooks';
import { useCustomers, useCustomersContext } from '@src/hooks/useCustomer';

import { every, identity } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { TransferSourceDestinationForm } from '../../../components/DetailsDrawer/Customer/CustomerTransferDetailsDrawer/TransferOverviewTab/TransferSourceDestinationForm';
import { BalanceTransactionPreview } from './BalanceTransactionPreview';
import {
  useCustomerBalanceData,
  useCustomerBalanceTransactionState,
  useFormHandlers,
  useTransactionSubmission,
} from './hooks';
import { getWithdrawableAmount, useAmountValidation } from './hooks/useAmountValidation';
import type { CustomerBalanceTransactionDrawerProps, CustomerTransactionForm } from './types';
import { FORCE_WITHDRAW_CONFIRM_TEXT, TRANSFER_TYPES } from './utils';

// Required fields for form submission
const REQUIRED_FIELDS = ['customer', 'customerAccount', 'transferType', 'currency', 'amount'] as const;

export const CUSTOMER_BALANCE_TRANSACTION_DRAWER_WIDTH = 550;

export function CustomerBalanceTransactionDrawer({
  onClose,
  formDefaults = EMPTY_OBJECT,
  showOnlyConfirmTransferFlow = false,
  entity,
  onConfirmedOverride,
}: CustomerBalanceTransactionDrawerProps) {
  const { customerTransactionAutoProposalMode } = useFeatureFlag();
  const { rejectPendingTransferTransaction } = useCustomersContext();

  // Create options for transfer type based on whether Set was primed
  const transferTypeOptions = useMemo(() => {
    const baseOptions: Array<{ value: CustomerLedgerUpdateTypeEnum; displayName: string }> = TRANSFER_TYPES.map(
      type => ({
        value: type,
        displayName: type,
      })
    );

    // Only include Set option if it was primed via transactionType prop
    if (formDefaults?.transferType === CustomerLedgerUpdateTypeEnum.Set) {
      baseOptions.push({
        value: CustomerLedgerUpdateTypeEnum.Set,
        displayName: 'Set',
      });
    }

    return baseOptions;
  }, [formDefaults.transferType]);

  // State management hook
  const { drawerState, confirmText, setConfirmText, updateDrawerState, isStateInitialized } =
    useCustomerBalanceTransactionState({ formDefaults });

  // Data hooks
  const customers = useCustomers();
  const { customerAccountBalance, customerMarketAccounts, customerMarketAccountsByName } = useCustomerBalanceData({
    drawerState,
  });

  // Form handlers hook
  const { handleCustomerChange, handleCustomerAccountChange, handleTransferTypeChange, verifyPastedValueIsNumber } =
    useFormHandlers({ drawerState, updateDrawerState, customerMarketAccounts });

  const { currenciesList } = useCurrenciesContext();
  const allCurrenciesOptions = useMemo(() => currenciesList.map(c => c.Symbol), [currenciesList]);

  const handleCurrencyChange = useCallback(
    (c: string | undefined) => {
      updateDrawerState({ currency: c, aliasCurrency: c });
    },
    [updateDrawerState]
  );

  // Handle configuration changes: if initiate method is selected but no longer available, switch to manual
  useEffect(() => {
    if (!isStateInitialized || !drawerState.transferMethod) {
      return;
    }

    // If "initiate" is selected but auto proposal mode doesn't support it, switch to "manual"
    if (
      drawerState.transferMethod === 'initiate' &&
      customerTransactionAutoProposalMode === TransactionAutoProposalModeEnum.None
    ) {
      updateDrawerState({ transferMethod: 'manual' });
    }
  }, [customerTransactionAutoProposalMode, drawerState.transferMethod, isStateInitialized, updateDrawerState]);

  // Transaction submission hook
  const { handlePreviewTransaction, handleConfirmTransaction } = useTransactionSubmission({
    drawerState,
    updateDrawerState,
    confirmText,
    onClose,
    onConfirmedOverride,
  });

  // Reject handler for showOnlyConfirmTransferFlow mode
  const handleRejectTransfer = useCallback(() => {
    if (entity == null) {
      return;
    }
    if (entity.Status === CustomerBalanceTransactionStatusEnum.PendingTransfer) {
      rejectPendingTransferTransaction(entity);
    }
  }, [entity, rejectPendingTransferTransaction]);

  // Preview disclosure for transaction preview
  const previewDisclosure = useDisclosureWithContext<{ form: CustomerTransactionForm; isForceWithdraw: boolean }>();

  // Validation logic using custom hook
  const amountValidationResult = useAmountValidation({
    amount: drawerState.amount,
    transferType: drawerState.transferType,
    customerBalance: customerAccountBalance,
    skipValidationForForceWithdraw: drawerState.forceWithdraw,
  });

  // [DEAL-6050] Ignore amount validation in showOnlyConfirmTransferFlow mode
  const amountError = showOnlyConfirmTransferFlow ? undefined : amountValidationResult;

  // Form validation logic
  const isFormValid = useMemo(() => {
    // Check all required fields are filled
    const requiredFieldsValid: boolean = every(REQUIRED_FIELDS, field => !!drawerState[field]);

    // If force withdraw is enabled or showOnlyConfirmTransferFlow is true, amount validation is not required
    const amountValid: boolean = drawerState.forceWithdraw || showOnlyConfirmTransferFlow ? true : amountError == null;

    // If force withdraw is enabled, the state is only valid if the confirm text is correct
    const forceWithdrawValid: boolean = drawerState.forceWithdraw ? confirmText === FORCE_WITHDRAW_CONFIRM_TEXT : true;

    return requiredFieldsValid && amountValid && forceWithdrawValid;
  }, [drawerState, amountError, confirmText, showOnlyConfirmTransferFlow]);

  if (!customers) {
    return null;
  }

  return (
    <Flex w="100%" overflowY="auto" flexDirection="column" justifyContent="space-between" h="100%">
      {showOnlyConfirmTransferFlow === false && (
        <Flex alignItems="center" justifyContent="space-between" w="100%" p="spacing16">
          <Text color="colorTextImportant" size="fontSizeLarge">
            New Balance Transaction
          </Text>
          <IconButton
            icon={IconName.Close}
            onClick={onClose}
            aria-label="Close"
            data-testid="new-transfer-form-close-button"
            ghost={true}
          />
        </Flex>
      )}
      <Divider />

      <DrawerContent overflowY="scroll" w="100%">
        {isStateInitialized ? (
          <Box>
            {/* Customer and Customer Account Row */}
            {showOnlyConfirmTransferFlow === false && (
              <Box w="100%">
                <HStack gap="spacing16">
                  <Box flex={1}>
                    <FormGroup label="Customer" mb="spacing16">
                      <SearchSelect
                        options={customers}
                        selection={customers.find(c => c?.Name === drawerState.customer)}
                        getLabel={customer => customer.DisplayName}
                        onChange={c => handleCustomerChange(c?.Name)}
                        data-testid="new-transfer-form-customer-select"
                        placeholder="Select Customer"
                        showClear
                      />
                    </FormGroup>
                  </Box>
                  <Box flex={1}>
                    <FormGroup label="Customer Account" mb="spacing16">
                      <SearchSelect
                        dropdownWidth={300}
                        selection={drawerState.customerAccount}
                        options={customerMarketAccounts}
                        getLabel={ma => customerMarketAccountsByName.get(ma)?.SourceAccountID || ma}
                        onChange={ma => handleCustomerAccountChange(ma)}
                        disabled={customerMarketAccounts.length === 1 || drawerState.customer == null}
                        data-testid="new-transfer-form-customer-account-select"
                        placeholder="Select Account"
                      />
                    </FormGroup>
                  </Box>
                </HStack>
                {/* Transfer Type Selector */}
                <FormGroup mb="spacing16" label="Transfer Type">
                  <ToggleHorizontal
                    value={drawerState.transferType}
                    options={transferTypeOptions}
                    onChange={handleTransferTypeChange}
                    data-testid="new-transfer-form-type-selector"
                    boxProps={{ w: '100%' }}
                    size={FormControlSizes.Default}
                  />
                </FormGroup>
              </Box>
            )}

            {/* Warning for Set transactions */}
            {drawerState.transferType === CustomerLedgerUpdateTypeEnum.Set && (
              <Box mb="spacing16">
                <Text color="colorTextWarning">
                  Setting a customer balance can impact balances if the customer is actively trading. Confirm this
                  customer has no open orders.
                </Text>
              </Box>
            )}

            {/* Transfer Details Section - Show only when customer, account, and type are selected */}
            {drawerState.customer && drawerState.customerAccount && drawerState.transferType ? (
              <Box w="100%">
                {showOnlyConfirmTransferFlow === false && (
                  <Box>
                    <Divider mt="spacing16" mb="spacing16" />
                    <HStack mb="spacing16" justifyContent="flex-start">
                      <Icon icon={IconName.ArrowLeftRight} />
                      <Text color="colorTextImportant" ml="spacing4">
                        Transfer Details
                      </Text>
                    </HStack>
                  </Box>
                )}

                {/* Asset and Amount Row - Shared for both Deposits and Withdrawals */}
                {showOnlyConfirmTransferFlow === false && (
                  <HStack gap="spacing16" mb="spacing16" alignItems="flex-start">
                    <Box flex={1}>
                      <FormGroup label="Asset*" mb="spacing16">
                        <SearchSelect
                          data-testid="new-transfer-form-currency-select"
                          selection={drawerState.currency}
                          options={allCurrenciesOptions}
                          onChange={handleCurrencyChange}
                          getLabel={identity}
                          placeholder="Select Asset"
                          showClear
                        />
                      </FormGroup>
                    </Box>
                    <Box flex={1}>
                      <FormGroup label="Amount*">
                        <NumberInput
                          min="0"
                          value={drawerState.amount ?? ''}
                          suffix={drawerState.currency}
                          onChange={value => updateDrawerState({ amount: value })}
                          disabled={!drawerState.currency}
                          touched={drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw}
                          invalid={
                            drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw && amountError != null
                          }
                          data-invalid={
                            drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw && amountError != null
                          }
                          data-testid="new-transfer-form-amount-input"
                          onPaste={verifyPastedValueIsNumber}
                          placeholder={drawerState.currency && 'Enter Amount'}
                        />

                        {/* Show error message for amount validation */}
                        {drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw && amountError && (
                          <Box mt="spacing2">
                            <Text color="colorTextWarning" fontSize="fontSizeSmall">
                              {amountError}
                            </Text>
                          </Box>
                        )}

                        {/* Show available balance only for withdrawals */}
                        {drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw &&
                          customerAccountBalance != null &&
                          drawerState.currency && (
                            <Box mt="spacing2">
                              <Text color="colorTextSubtle" fontSize="fontSizeSmall">
                                Available Customer Balance:{' '}
                                <InlineFormattedNumber
                                  number={getWithdrawableAmount(customerAccountBalance)}
                                  currency={customerAccountBalance.Currency}
                                />
                              </Text>
                            </Box>
                          )}
                      </FormGroup>
                    </Box>
                  </HStack>
                )}
                <TransferSourceDestinationForm
                  drawerState={drawerState}
                  updateDrawerState={updateDrawerState}
                  showOnlyConfirmTransferFlow={showOnlyConfirmTransferFlow}
                />
                {/* Force Withdraw Section (only for withdrawals) */}
                {drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw && !showOnlyConfirmTransferFlow && (
                  <Box data-testid="force-withdraw-section" mt="spacing16">
                    <Flex justifyContent="space-between" alignItems="center" mb="spacing16">
                      <Text>Force Withdraw</Text>
                      <Toggle
                        checked={drawerState.forceWithdraw || false}
                        onChange={checked => {
                          updateDrawerState({ forceWithdraw: checked });
                          if (!checked) {
                            setConfirmText('');
                          }
                        }}
                        data-testid="new-transfer-form-force-withdraw-toggle"
                      />
                    </Flex>
                    {drawerState.forceWithdraw && (
                      <VStack w="100%" alignItems="flex-start">
                        <NotificationInternalToast variant={NotificationVariants.Negative} textAlign="left">
                          Initiating a forced withdrawal bypasses all validations. Please use this option with caution.
                        </NotificationInternalToast>
                        <FormGroup
                          label={`To confirm, type "${FORCE_WITHDRAW_CONFIRM_TEXT}" below:`}
                          w="100%"
                          mt="spacing8"
                        >
                          <Input
                            value={confirmText}
                            autoComplete="off"
                            onChange={e => setConfirmText(e.target.value)}
                            data-testid="new-transfer-form-confirm-input"
                            placeholder={`Type '${FORCE_WITHDRAW_CONFIRM_TEXT}'`}
                          />
                        </FormGroup>
                      </VStack>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              // Dynamically create this list from customer, account, and transfer type
              <Text>
                Please select{' '}
                {!drawerState.customer ? 'a customer' : !drawerState.customerAccount ? 'an account' : 'a transfer type'}
                .
              </Text>
            )}
          </Box>
        ) : (
          <Flex justifyContent="center" alignItems="center" h="200px">
            <LoaderTalos delay={300} />
          </Flex>
        )}
      </DrawerContent>

      {/* Footer Buttons */}
      <DrawerFooter>
        <HStack w="100%" gap="spacing16">
          {/*Show "Reject" button when showOnlyConfirmTransferFlow is true, otherwise show "Close" button */}
          <Button
            onClick={showOnlyConfirmTransferFlow ? handleRejectTransfer : onClose}
            variant={showOnlyConfirmTransferFlow ? ButtonVariants.Negative : ButtonVariants.Default}
            flex={1}
            data-testid="new-transfer-form-close-button"
          >
            {showOnlyConfirmTransferFlow ? 'Reject' : 'Close'}
          </Button>
          <Button
            variant={ButtonVariants.Primary}
            flex={1}
            disabled={!isFormValid}
            onClick={handlePreviewTransaction(previewDisclosure)}
            data-testid={`new-transfer-form-preview-${
              drawerState.transferType === CustomerLedgerUpdateTypeEnum.Deposit
                ? 'deposit'
                : drawerState.transferType === CustomerLedgerUpdateTypeEnum.Set
                  ? 'set'
                  : drawerState.transferMethod === 'initiate'
                    ? 'transfer'
                    : 'manual-transfer'
            }-button`}
          >
            {/* Use "Initiate" keyword instead of "Preview" when showOnlyConfirmTransferFlow is true */}
            {drawerState.transferType === CustomerLedgerUpdateTypeEnum.Deposit
              ? 'Preview Deposit'
              : drawerState.transferType === CustomerLedgerUpdateTypeEnum.Set
                ? 'Preview Set'
                : drawerState.transferMethod === 'initiate'
                  ? showOnlyConfirmTransferFlow
                    ? 'Initiate Transfer'
                    : 'Preview Transfer'
                  : showOnlyConfirmTransferFlow
                    ? 'Initiate Manual Transfer'
                    : 'Preview Manual Transfer'}
          </Button>
        </HStack>
      </DrawerFooter>

      <BalanceTransactionPreview
        previewDisclosure={previewDisclosure}
        onConfirmTransaction={handleConfirmTransaction(previewDisclosure)}
      />
    </Flex>
  );
}
